import { useEffect } from "react";

import * as Prismic from "@prismicio/client";
import * as PrismicNext from "@prismicio/next";
import { KeyTextField, RichTextField } from "@prismicio/types";
import _get from "lodash/get";
import { get, renderFn } from "micromustache";

import {
  useGetCustomisation,
  useGetSchema,
} from "../queries/legacy/product-queries";
import sm from "../slicemachine.config.json";
import { CharactersSchemaField } from "../types/schema";

import { PRISMIC_CONFIGURATION_ERROR } from "./error-utils";

/**
 * The project's Prismic repository name.
 */
export const repositoryName = Prismic.getRepositoryName(sm.apiEndpoint);

/**
 * Creates a Prismic client for the project's repository. The client is used to
 * query content from the Prismic API.
 */
export function prismic(config: PrismicNext.CreateClientConfig) {
  if (!process.env.PRISMIC_API_TOKEN) {
    throw PRISMIC_CONFIGURATION_ERROR;
  }

  const client = Prismic.createClient(sm.apiEndpoint, {
    accessToken: process.env.PRISMIC_API_TOKEN,
    ...config,
  });

  PrismicNext.enableAutoPreviews({
    client,
    previewData: config.previewData,
    req: config.req,
  });

  return client;
}

/**
 * Get the marketplace (used in routing) from the (lowercase) Prismic doc lang
 */
export function getMarketplaceFromPrismicLang(lang: string) {
  switch (lang.toLowerCase()) {
    case "en-us":
      return "us";
    case "en-gb":
      return "uk";
    case "en-au":
      return "au";
    case "en-ca":
      return "ca";
    case "da-dk":
      return "dk";
    case "de-de":
      return "de";
    case "es-es":
      return "es";
    case "es-mx":
      return "mx";
    case "fr-fr":
      return "fr";
    case "it-it":
      return "it";
    case "ja-jp":
      return "jp";
    case "ko-kr":
      return "kr";
    case "nl-nl":
      return "nl";
    case "pt-pt":
      return "pt";
    case "sv-se":
      return "se";
    default:
      return "us";
  }
}

/**
 * Get the correct product language locale from marketplace code in the URL
 */
export function getLocaleFromMarketplace(marketplace: string) {
  switch (marketplace) {
    case "us":
      return "en-US";
    case "ca":
      return "en-US";
    case "uk":
      return "en-GB";
    case "ie":
      return "en-GB";
    case "au":
      return "en-GB";
    case "dk":
      return "da";
    case "de":
      return "de";
    case "es" || "mx":
      return "es";
    case "fr":
      return "fr";
    case "it":
      return "it";
    case "jp":
      return "ja";
    case "kr":
      return "ko";
    case "nl":
      return "nl";
    case "pt":
      return "pt-PT";
    case "se":
      return "sv";
    default:
      return "en-US";
  }
}

/**
 * Type guard to check if a field is Rich Text
 * (i.e. an array of tags, rather than a string)
 */
export function isRichText(
  field: KeyTextField | RichTextField
): field is RichTextField {
  return Array.isArray(field as RichTextField);
}

/**
 * Objects for looking up in the LABEL helper below
 */
const labels: Record<string, Record<string, string>> = {
  locale: {
    "en-GB": "English (UK)",
    "en-US": "English (US)",
    fr: "Français",
    da: "Dansk",
    de: "Deutsch",
    es: "Español",
    it: "Italiano",
    ja: "日本",
    nl: "Nederlands",
    "pt-PT": "Português",
    sv: "Svenska",
    ko: "한국어",
  },
};

/**
 * Custom render functions for micromustache.
 *
 * Functions start with an identifier, then args separated with a space.
 * This is similar to Handlebar's format.
 *
 * e.g. {{CURRENCY 100}} -> ['CURRENCY', '100'] -> '£100.00'
 *
 * TODO: improve robustness and error handling
 */
function helpers(path: string, scope?: Record<string, any>) {
  switch (path.split(" ")[0]) {
    case "CURRENCY": {
      const [, amount] = path.split(" ");

      return new Intl.NumberFormat(
        getLocaleFromMarketplace(scope?.marketplace as string),
        {
          style: "currency",
          currency: "gbp",
        }
      ).format(parseInt(amount, 10));
    }
    case "DATE": {
      const [, year, month, day, locale] = path.split(" ");

      return new Date(
        Date.UTC(
          parseInt(scope?.[year] as string, 10),
          parseInt(scope?.[month] as string, 10) - 1,
          parseInt(scope?.[day] as string, 10)
        )
      ).toLocaleDateString(locale);
    }
    case "LABEL": {
      const [, key] = path.split(" ");
      const value = get(scope || {}, key);

      return labels[key][value];
    }
    case "LIST": {
      const [, key] = path.split(" ");

      // {{LIST avatars}} will fetch the `name` of every avatar for display
      if (key === "avatars" && scope?.avatars) {
        return Object.values(scope.avatars)
          .map((value) => (value as any).name)
          .join(", ")
          .replace(/, ([^,]*)$/, " & $1");
      }

      const variable = get(scope || {}, key);

      if (Array.isArray(variable)) {
        // Combine the entries with commas and replace the final comma with an ampersand
        return variable.join(", ").replace(/, ([^,]*)$/, " & $1");
      }

      return variable;
    }
    case "TRY": {
      const [, ...attempts] = path.split(" ");

      // Return the first entry in `attempts` that exists in the scope
      return get(
        scope || {},
        attempts.find((a) => (scope || {})[a]) || "undefined"
      );
    }
    default: {
      // `get` performs the default interpolation
      return get(scope || {}, path);
    }
  }
}

function useGetAllAvatarData() {
  const { data: schema } = useGetSchema();
  const { data: customisation } = useGetCustomisation();

  if (!schema || !customisation || !schema.keys.characters) {
    return {};
  }

  const allAvatars: { [role: string]: { [key: string]: any } } = {};

  (schema.keys.characters as CharactersSchemaField).ordered?.forEach(
    (characterType, i) => {
      characterType.keys.customisations.ordered?.forEach((character, j) => {
        allAvatars[character.metas[0].role] = {};

        Object.keys(character.keys).forEach((key) => {
          allAvatars[character.metas[0].role][key] = _get(
            customisation,
            `characters[${i}].customisations[${j}].${key}`
          );
        });
      });
    }
  );

  return allAvatars;
}

/**
 * Interpolate values in the field.
 *
 * Automatically provides URL customisation for the scope, but can be provided more
 */
export function useInterpolate() {
  const { data: customisation } = useGetCustomisation();
  const avatars = useGetAllAvatarData();

  function interpolate(
    text: string | number,
    additionalScope?: Record<string, any>
  ): string;
  function interpolate(
    text: string[],
    additionalScope?: Record<string, any>
  ): string[];

  function interpolate(
    text: string | string[] | number,
    additionalScope?: Record<string, any>
  ) {
    const format = (str: string | number) => {
      // usage has shown that `text` could be a number here - e.g. a age value
      return renderFn(String(str), helpers, {
        ...customisation,
        ...(additionalScope ?? {}),
        ...(avatars ? { avatars } : {}),
      });
    };

    if (Array.isArray(text)) {
      return text.map((i) => format(i));
    }

    return format(text);
  }

  return interpolate;
}

/**
 * I know this is ridiculous but the Prismic toolbar is driving me crazy.
 * It sits directly on top of the drawers and offers no way to change it.
 *
 * So let's watch the whole document for mutations and shift it when it shows up.
 */
export function useReStylePrismicToolbar() {
  useEffect(() => {
    const targetNode = document.querySelector("body");

    const observer = new MutationObserver((data) => {
      // When a mutation occurs, check if there's a node with the ID `prismic-toolbar-v2`
      data.forEach(({ addedNodes }) => {
        addedNodes.forEach((node) => {
          if (
            "id" in node &&
            (node as any).id === "prismic-toolbar-v2" &&
            "shadowRoot" in node
          ) {
            // If so, target the `Toolbar` and increase its distance from the drawers
            ((node as any).shadowRoot as ShadowRoot)
              ?.querySelector(".Toolbar")
              ?.setAttribute("style", "bottom: 172px; left: 16px;");
            // And stop the `PreviewMenu` from spilling over on smaller screens
            ((node as any).shadowRoot as ShadowRoot)
              ?.querySelector(".PreviewMenu")
              ?.setAttribute(
                "style",
                "max-width: calc(100vw - 32px); box-sizing: border-box;"
              );
          }
        });
      });
    });

    observer.observe(targetNode!, { childList: true });

    return () => {
      observer.disconnect();
    };
  }, []);
}
